import { StreamingStateEnum } from '@/data/webrtc-enums';
import { makeAutoObservable, runInAction } from 'mobx';
import { securePTTService } from '@/services/secure-ptt-service';
import { PTTEvent } from '@/types/ptt-event';
import { PTTStatus } from '@/types/ptt-status';

interface Streamer {
  emitUIInteraction: (data: any) => void;
  connect: () => void;
  play: () => void;
  addEventListener: (event: string, callback: (event?: any) => void) => void;
  removeEventListener: (event: string, callback: (event?: any) => void) => void;
}

export type TextResponseCallback = (response: string) => void;
export type CourseCardOverlayItem = {
  courseId: string;
  title: string;
  summary: string;
  score?: number;
  goalIndex?: number;
};
export type GoalCardItem = {
  goalId: string;
  title: string;
  selectedCourseId?: string;
  selectedCourseTitle?: string;
};

export default class DigitalHumanStore {
  streamingState = StreamingStateEnum.WebRtcDisconnected;
  private streamer: Streamer | null = null;
  
  // PTT Service integration (singleton)
  private pttService = securePTTService;
  currentPTTEvent: PTTEvent | null = null;
  currentPTTStatus: PTTStatus | null = null;

  listResponseListener: TextResponseCallback[] = [];
  onStateChanged?: (state: StreamingStateEnum) => void;

  // Audio management properties
  private audioContext: AudioContext | null = null;
  private gatedMediaStream: MediaStream | null = null;
  private gainNode: GainNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private microphoneUnavailable = false; // Flag to prevent repeated attempts when no mic
  private userRequestedAudioGateOpen = false;
  private assistantSpeechSuppressesMic = false;
  private assistantSpeechStartedAtMs = 0;
  private readonly assistantSuppressedMicGain = Number(
    process.env.NEXT_PUBLIC_BARGE_IN_DUCK_GAIN ?? '0.12'
  );

  /** When true, app does not request the microphone (e.g. when presenting in Teams). */
  presentationMode = false;
  qrCodeVisible = false;
  qrCodeText = '';
  qrCodeUrl = '';
  private qrHideTimeout: ReturnType<typeof setTimeout> | null = null;
  goalCards: GoalCardItem[] = [];
  goalCardsVisible = false;
  activeGoalIndex: number | null = null;
  courseCardsVisible = false;
  courseCardsFading = false;
  courseCards: CourseCardOverlayItem[] = [];
  private courseCardsHideTimeout: ReturnType<typeof setTimeout> | null = null;
  keywordsVisible = false;
  keywords: string[] = [];
  private keywordsHideTimeout: ReturnType<typeof setTimeout> | null = null;
  selectedCourseId: string | null = null;
  private selectedCourseTimeout: ReturnType<typeof setTimeout> | null = null;

  coachingPhaseIndex = -1;
  coachingPhaseLabel = '';
  progressBarRevealed = false;
  visitedPhaseIndices: Set<number> = new Set();
  phaseBannerVisible = false;
  phaseBannerText = '';
  private phaseBannerTimeout: ReturnType<typeof setTimeout> | null = null;
  sessionStartedAt: number | null = null;

  subtitleText = '';

  connectionStatusMessage: string | null = null;

  
  constructor() {
    makeAutoObservable(this);
    
    // Only run browser-specific code if window exists
    if (typeof window !== 'undefined') {
      // Initialize PTT Service (client-side only)
      this.initializePTTService().catch(err => {
        console.error('Failed to initialize PTTService:', err);
      });
    }
  }

  private async initializePTTService() {
    try {
      console.log('🎙️ Initializing PTTService (singleton)...');
      
      // Set up callbacks for this store instance
      this.pttService.onPTTEvent((event) => {
        this.setCurrentPTTEvent(event);
      });

      this.pttService.onPTTStatus((status) => {
        this.setCurrentPTTStatus(status);
      });

      // Initialize the singleton service (will only happen once)
      await this.pttService.initialize();
      console.log('🎙️ PTTService (singleton) initialized');
    } catch (error) {
      console.error('🎙️ PTTService initialization error:', error);
      throw error;
    }
  }

  private setCurrentPTTEvent(event: PTTEvent) {
    this.currentPTTEvent = event;
    console.log('🎙️ PTT Event received:', event);
    
    // Handle PTT events here - e.g., open/close audio gate based on press/release
    if (event.type === 'press') {
      console.log('🎙️ PTT pressed - opening audio gate');
      this.openAudioGate();
    } else if (event.type === 'release') {
      console.log('🎙️ PTT released - closing audio gate');
      this.closeAudioGate();
    }
  }

  private setCurrentPTTStatus(status: PTTStatus) {
    this.currentPTTStatus = status;
    console.log('🎙️ PTT Status updated:', status);
  }

  getStreamingState() {
    return this.streamingState;
  }

  setStreamingState(state: StreamingStateEnum) {
    console.log(`StreamingState: ${state}`);
    this.streamingState = state;
    if (this.onStateChanged != undefined) {
      this.onStateChanged(state);
    }
  }

  setConnectionStatusMessage(message: string | null) {
    this.connectionStatusMessage = message;
  }

  setStreamer(client: Streamer) {
    this.streamer = client;
  }

  connect() {
    if (this.streamer) {
      try {
        this.streamer.addEventListener('webRtcConnected', () => {
          console.log('WebRTC connected');
        });
        
        // Add listener for WebRTC messages
        this.streamer.addEventListener('message', (event: any) => {
          console.log('WebRTC message received:', event);
          // Notify all response listeners
          this.listResponseListener.forEach(listener => {
            try {
              listener(event);
            } catch (error) {
              console.error('Error in response listener:', error);
            }
          });
        });
        
        this.streamer.addEventListener('error', (error: Event) => {
          console.error('WebSocket error:', error);
        });
        
        this.streamer.addEventListener('close', (event: CloseEvent) => {
          console.log('WebSocket closed:', event);
        });

        this.streamer.connect();
        
      } catch (error) {
        console.error('Connection failed:', error);
        this.setStreamingState(StreamingStateEnum.WebRtcDisconnected);
      }
    } else {
      console.warn('Streamer not initialized');
    }
  }

  startStreaming() {
    this.streamer?.play();
  }


  // PTT Service getters
  getCurrentPTTEvent(): PTTEvent | null {
    return this.currentPTTEvent;
  }

  getCurrentPTTStatus(): PTTStatus | null {
    return this.currentPTTStatus;
  }

  isPTTServiceConnected(): boolean {
    return this.pttService.getConnectionStatus();
  }

  getPTTService() {
    return this.pttService;
  }

  getStreamer() {
    return this.streamer;
  }

  // Audio management methods
  async resumeAudioContext(): Promise<void> {
    try {
      if (!this.audioContext) {
        console.warn('⚠️ No AudioContext to resume');
        return;
      }
      
      if (this.audioContext.state === 'suspended') {
        console.log('🔊 Resuming AudioContext on user gesture...');
        await this.audioContext.resume();
        console.log('✅ AudioContext resumed successfully');
      } else {
        console.log('🔊 AudioContext already running');
      }
    } catch (error) {
      console.error('❌ Failed to resume AudioContext:', error);
      // Don't throw - continue with suspended context
    }
  }

  setPresentationMode(enabled: boolean): void {
    this.presentationMode = enabled;
    console.log('🎚️ Presentation mode:', enabled ? 'on (mic disabled)' : 'off');
  }

  async createGatedMediaStream(): Promise<MediaStream | null> {
    // Don't claim the microphone when presenting (e.g. in Teams)
    if (this.presentationMode) {
      return null;
    }

    // Return existing stream if already created
    if (this.gatedMediaStream) {
      return this.gatedMediaStream;
    }
    
    // Don't retry if we already know the microphone is unavailable
    if (this.microphoneUnavailable) {
      return null;
    }
    
    console.log('🎚️ Starting gated media stream creation...');
    
    try {
      // First, check if any audio input devices are available
      console.log('🎚️ Checking for available audio input devices...');
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      if (audioInputs.length === 0) {
        console.warn('⚠️ No microphone found. Please connect a microphone and reload the page.');
        console.warn('⚠️ On macOS: System Settings → Privacy & Security → Microphone → Enable for your browser');
        this.microphoneUnavailable = true;
        return null;
      }
      
      console.log(`🎚️ Found ${audioInputs.length} audio input device(s)`);
      
      console.log('🎚️ Checking AudioContext...');
      if (!this.audioContext) {
        console.log('🎚️ Creating AudioContext (without resume)...');
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      if (!this.audioContext) {
        console.error('❌ AudioContext not available for gated stream creation');
        return null;
      }
      
      console.log('🎚️ AudioContext created, state:', this.audioContext.state);
      
      // AudioContext will be suspended due to autoplay policy - that's expected
      // It will be resumed when the user first presses PTT
      if (this.audioContext.state === 'suspended') {
        console.log('🎚️ AudioContext suspended (expected) - will resume on PTT press');
      }

      // Get microphone stream
      console.log('🎚️ Requesting microphone access...');
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: 1,
          sampleRate: { ideal: 24000 }
        } 
      });
      
      console.log('🎚️ Microphone stream obtained, tracks:', micStream.getAudioTracks().length);

      // Create audio nodes for gating
      console.log('🎚️ Creating audio nodes...');
      this.sourceNode = this.audioContext.createMediaStreamSource(micStream);
      this.gainNode = this.audioContext.createGain();
      
      // Start with audio gated (gain = 0)
      this.gainNode.gain.value = 0;
      console.log('🎚️ Gain node created, initial gain:', this.gainNode.gain.value);
      
      // Connect source to gain node
      this.sourceNode.connect(this.gainNode);
      console.log('🎚️ Source connected to gain node');
      
      // Create destination for the gated stream
      const destination = this.audioContext.createMediaStreamDestination();
      this.gainNode.connect(destination);
      console.log('🎚️ Gain node connected to destination');
      
      // Store the gated stream
      this.gatedMediaStream = destination.stream;
      
      console.log('🎚️ ✅ Created gated media stream with gain control');
      console.log('🎚️ Gated stream tracks:', this.gatedMediaStream.getAudioTracks().length);
      return this.gatedMediaStream;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorName = error instanceof Error ? error.name : 'Unknown';
      
      console.error('❌ Failed to create gated media stream:', error);
      
      // Provide specific guidance based on error type
      if (errorName === 'NotFoundError') {
        console.error('❌ No microphone found. Troubleshooting steps:');
        console.error('   1. Check that a microphone is connected');
        console.error('   2. macOS: System Settings → Privacy & Security → Microphone → Enable for browser');
        console.error('   3. Check Sound settings to verify microphone is detected');
        this.microphoneUnavailable = true; // Don't retry - mic not found
      } else if (errorName === 'NotAllowedError') {
        console.error('❌ Microphone permission denied. Please allow microphone access in your browser.');
        this.microphoneUnavailable = true; // Don't retry - permission denied
      } else if (errorName === 'NotReadableError') {
        console.error('❌ Microphone is in use by another application.');
        // Don't set flag - might become available later
      } else {
        console.error('❌ Error details:', errorMessage);
      }
      
      return null;
    }
  }

  getGatedMediaStream(): MediaStream | null {
    return this.gatedMediaStream;
  }

  setGatedMediaStream(stream: MediaStream): void {
    this.gatedMediaStream = stream;
    console.log('🎚️ Gated media stream set');
  }

  // Audio gating control methods
  private async applyAudioGateState(open: boolean): Promise<void> {
    if (this.gainNode && this.audioContext) {
      if (open) {
        // Try to resume AudioContext if it's suspended (user gesture available now)
        await this.resumeAudioContext();
        this.gainNode.gain.setValueAtTime(1.0, this.audioContext.currentTime);
        console.log('🎚️ Audio gate OPENED (gain = 1.0)');
      } else {
        const shouldDuckForBargeIn =
          this.assistantSpeechSuppressesMic && this.userRequestedAudioGateOpen;
        const targetGain = shouldDuckForBargeIn ? this.assistantSuppressedMicGain : 0.0;
        this.gainNode.gain.setValueAtTime(targetGain, this.audioContext.currentTime);
        console.log(
          shouldDuckForBargeIn
            ? `🎚️ Audio gate SUPPRESSED for barge-in (gain = ${targetGain.toFixed(2)})`
            : '🎚️ Audio gate CLOSED (gain = 0.0)'
        );
      }
    } else {
      console.warn('⚠️ Cannot update audio gate - gain node not available');
    }
  }

  async openAudioGate(): Promise<void> {
    this.userRequestedAudioGateOpen = true;
    if (this.assistantSpeechSuppressesMic) {
      // Keep gate closed while assistant audio is playing.
      return;
    }
    await this.applyAudioGateState(true);
  }

  closeAudioGate(): void {
    this.userRequestedAudioGateOpen = false;
    void this.applyAudioGateState(false);
  }

  suppressMicDuringAssistantSpeech(): void {
    if (this.assistantSpeechSuppressesMic) {
      return;
    }
    this.assistantSpeechSuppressesMic = true;
    this.assistantSpeechStartedAtMs = Date.now();
    void this.applyAudioGateState(false);
  }

  releaseMicAfterAssistantSpeech(): void {
    if (!this.assistantSpeechSuppressesMic) {
      return;
    }
    this.assistantSpeechSuppressesMic = false;
    this.assistantSpeechStartedAtMs = 0;
    if (this.userRequestedAudioGateOpen) {
      void this.applyAudioGateState(true);
    }
  }

  isAssistantSpeechSuppressed(): boolean {
    return this.assistantSpeechSuppressesMic;
  }

  getAssistantSpeechStartedAtMs(): number {
    return this.assistantSpeechStartedAtMs;
  }

  isAudioGateOpen(): boolean {
    return this.gainNode ? this.gainNode.gain.value > 0 : false;
  }

  // ASR lifecycle callbacks - these will be called by the speech recognition system
  onAsrRecognized(): void {
    console.log('🎤 ASR: Speech recognized - user message forwarded to bot backend');
    // This callback is triggered after the transcribed user message has been sent to the bot
    // Additional UI state changes or analytics could be added here
  }

  onAsrRecognizing(): void {
    console.log('🎤 ASR: Speech recognizing...');
    // This could be used to show "listening" indicators
  }

  onAsrCanceled(reason: any): void {
    console.log('🎤 ASR: Speech recognition canceled:', reason);
    // This could be used to handle errors or cleanup
  }

  cleanup() {
    console.log('Cleaning up DigitalHumanStore resources');
    
    // Clear any pending timeouts
    if (this.qrHideTimeout) {
      clearTimeout(this.qrHideTimeout);
      this.qrHideTimeout = null;
    }
    if (this.courseCardsHideTimeout) {
      clearTimeout(this.courseCardsHideTimeout);
      this.courseCardsHideTimeout = null;
    }
    if (this.keywordsHideTimeout) {
      clearTimeout(this.keywordsHideTimeout);
      this.keywordsHideTimeout = null;
    }
    if (this.selectedCourseTimeout) {
      clearTimeout(this.selectedCourseTimeout);
      this.selectedCourseTimeout = null;
    }
    if (this.phaseBannerTimeout) {
      clearTimeout(this.phaseBannerTimeout);
      this.phaseBannerTimeout = null;
    }
    
    // Note: PTT service is a singleton, so we don't clean it up here
    // It will be cleaned up automatically on hot reload or app shutdown
    
    // Clean up audio resources
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    
    if (this.gatedMediaStream) {
      this.gatedMediaStream.getTracks().forEach(track => track.stop());
      this.gatedMediaStream = null;
    }
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(err => 
        console.error('Error closing AudioContext:', err)
      );
      this.audioContext = null;
    }
    
    // Reset state
    this.streamingState = StreamingStateEnum.WebRtcDisconnected;
    this.microphoneUnavailable = false;
    this.goalCards = [];
    this.goalCardsVisible = false;
    this.activeGoalIndex = null;
    
    console.log('DigitalHumanStore cleanup completed');
  }

  hideQRCode() {
    this.qrCodeVisible = false;
    this.qrCodeText = '';
    this.qrCodeUrl = '';
    if (this.qrHideTimeout) {
      clearTimeout(this.qrHideTimeout);
      this.qrHideTimeout = null;
    }
  }

  /**
   * Display a QR code with optional auto-hide duration.
   * @param text The text to display with the QR code
   * @param url The URL to encode in the QR code
   * @param durationMs Optional duration in milliseconds (default: 20000)
   */
  displayQRCode(text: string, url: string, durationMs: number = 20000) {
    if (!text || !url) {
      return Promise.resolve();
    }

    this.qrCodeText = text;
    this.qrCodeUrl = url;
    this.qrCodeVisible = true;

    if (this.qrHideTimeout) {
      clearTimeout(this.qrHideTimeout);
      this.qrHideTimeout = null;
    }

    return new Promise<void>((resolve) => {
      this.qrHideTimeout = setTimeout(() => {
        this.hideQRCode();
        resolve();
      }, durationMs);
    });
  }

  hideCourseCards() {
    if (this.courseCardsHideTimeout) {
      clearTimeout(this.courseCardsHideTimeout);
      this.courseCardsHideTimeout = null;
    }
    this.courseCardsFading = true;
    this.courseCardsHideTimeout = setTimeout(() => {
      runInAction(() => {
        this.courseCardsVisible = false;
        this.courseCardsFading = false;
        this.courseCards = [];
        this.selectedCourseId = null;
        this.courseCardsHideTimeout = null;
      });
    }, 500);
  }

  displayCourseCards(cards: CourseCardOverlayItem[]) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return;
    }

    if (this.courseCardsHideTimeout) {
      clearTimeout(this.courseCardsHideTimeout);
      this.courseCardsHideTimeout = null;
    }

    this.courseCards = cards.slice(0, 3);
    this.courseCardsFading = false;
    this.courseCardsVisible = true;
  }

  hideKeywords() {
    this.keywordsVisible = false;
    this.keywords = [];
    if (this.keywordsHideTimeout) {
      clearTimeout(this.keywordsHideTimeout);
      this.keywordsHideTimeout = null;
    }
  }

  showKeywords(keywords: string[], durationMs: number = 0) {
    const cleaned = (keywords || [])
      .map(item => String(item).trim())
      .filter(Boolean)
      .slice(0, 5);

    if (cleaned.length === 0) {
      return Promise.resolve();
    }

    this.keywords = cleaned;
    this.keywordsVisible = true;

    if (this.keywordsHideTimeout) {
      clearTimeout(this.keywordsHideTimeout);
      this.keywordsHideTimeout = null;
    }

    // durationMs === 0 means "keep until explicitly hidden via hideKeywords()"
    if (durationMs > 0) {
      return new Promise<void>((resolve) => {
        this.keywordsHideTimeout = setTimeout(() => {
          this.hideKeywords();
          resolve();
        }, durationMs);
      });
    }

    return Promise.resolve();
  }

  startSessionTimer() {
    if (!this.sessionStartedAt) {
      this.sessionStartedAt = Date.now();
    }
  }

  revealProgressBar() {
    this.progressBarRevealed = true;
  }

  advanceCoachingPhase(phaseIndex: number, label: string) {
    if (phaseIndex >= 0) {
      // Mark all phases up to (but not including) the current one as visited,
      // so skipped phases also light up correctly in the progress bar.
      for (let i = 0; i < phaseIndex; i++) {
        this.visitedPhaseIndices.add(i);
      }
      this.visitedPhaseIndices.add(phaseIndex);
    }
    this.coachingPhaseIndex = phaseIndex;
    this.coachingPhaseLabel = label;

    if (this.phaseBannerTimeout) {
      clearTimeout(this.phaseBannerTimeout);
      this.phaseBannerTimeout = null;
    }

    this.phaseBannerText = label;
    this.phaseBannerVisible = true;

    this.phaseBannerTimeout = setTimeout(() => {
      runInAction(() => {
        this.phaseBannerVisible = false;
        this.phaseBannerText = '';
        this.phaseBannerTimeout = null;
      });
    }, 2800);
  }

  selectCourseCard(courseId: string) {
    if (this.selectedCourseTimeout) {
      clearTimeout(this.selectedCourseTimeout);
      this.selectedCourseTimeout = null;
    }

    this.selectedCourseId = courseId;

    if (this.courseCardsHideTimeout) {
      clearTimeout(this.courseCardsHideTimeout);
      this.courseCardsHideTimeout = null;
    }

    this.courseCardsHideTimeout = setTimeout(() => {
      runInAction(() => {
        this.hideCourseCards();
        this.selectedCourseId = null;
      });
    }, 5000);
  }

  addGoalCard(goalId: string, title: string) {
    const existingIndex = this.goalCards.findIndex(g => g.goalId === goalId);
    if (existingIndex >= 0) {
      // Replace existing goal (e.g. user going back to change a goal)
      this.goalCards = this.goalCards.map((g, i) =>
        i === existingIndex ? { goalId, title } : g
      );
    } else {
      if (this.goalCards.length >= 4) return;
      this.goalCards = [...this.goalCards, { goalId, title }];
    }
    this.goalCardsVisible = true;
  }

  clearGoalCards() {
    this.goalCards = [];
    this.goalCardsVisible = false;
    this.activeGoalIndex = null;
  }

  attachCourseToGoal(goalIndex: number, courseId: string, courseTitle: string) {
    if (goalIndex < 0 || goalIndex >= this.goalCards.length) return;
    this.goalCards = this.goalCards.map((g, i) =>
      i === goalIndex ? { ...g, selectedCourseId: courseId, selectedCourseTitle: courseTitle } : g
    );
    this.hideCourseCards();
  }

  displayCourseCardsForGoal(cards: CourseCardOverlayItem[], goalIndex: number) {
    if (!Array.isArray(cards) || cards.length === 0) return;
    if (this.courseCardsHideTimeout) {
      clearTimeout(this.courseCardsHideTimeout);
      this.courseCardsHideTimeout = null;
    }
    this.courseCards = cards.slice(0, 3).map(c => ({ ...c, goalIndex }));
    this.activeGoalIndex = goalIndex;
    this.courseCardsFading = false;
    this.courseCardsVisible = true;
  }

  appendSubtitle(delta: string) {
    this.subtitleText += delta;
  }

  clearSubtitle() {
    this.subtitleText = '';
  }

}
